"use client";

import React from "react";
import { ComponentProps } from "./types";
import { MODAL_COMPONENT_SIZE, RESCHEDULE_LIST } from "./consts";
import Button from "antd/es/button";
import Flex from "antd/es/flex";
import Form from "antd/es/form";
import Text from "antd/es/typography/Text";
import Paragraph from "antd/es/typography/Paragraph";
import { useLocale } from "@/lib/i18n";
import { bookingFlowApi } from "@/lib/api/bookingFlowApi";
import { extractErrorMessage } from "@/lib/helpers/error";
import InlineError from "./components/InlineError";
import InlineNotice from "./components/InlineNotice";
import VerificationResendButton from "./components/VerificationResendButton";
import VerificationCodeInput from "./components/VerificationCodeInput";
import { useResendCooldown } from "./useResendCooldown";
import { isVerificationCodeAlreadySentError } from "./verificationSendError";
import styles from "./appointmentFlow.module.css";

const RescheduleMain: React.FC<ComponentProps> = ({
  appointmentDetails,
  state,
  setState,
  onCreateAppointment,
}) => {
  const { t } = useLocale();
  const {
    isCoolingDown: isResendCoolingDown,
    startCooldown: startResendCooldown,
  } = useResendCooldown();
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [isSending, setSending] = React.useState(false);
  const [verificationStarted, setVerificationStarted] = React.useState(false);
  const [code, setCode] = React.useState("");
  const [codeError, setCodeError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [codeAlreadySent, setCodeAlreadySent] = React.useState(false);
  const {
    paymentMethodId,
    customerId,
    phoneNumber,
    email,
    rescheduleVerificationPurpose,
  } = state.state;
  const verificationMethod = appointmentDetails.verificationMethod ?? "SMS";
  const isEmail = verificationMethod === "EMAIL";
  const handleNewAppointment = () => {
    setSubmitting(true);
    if (!customerId) {
      setSubmitting(false);
      return;
    }
    onCreateAppointment({
      customerId,
      paymentMethodId,
      errorCallback: () => {
        setSubmitting(false);
      },
    });
  };

  // Every successful send starts the cooldown, the automatic one included:
  // the server refuses another send for 45s, so an enabled Re-send could only
  // fail (BF-DRIFT-010).
  const sendVerificationCode = React.useCallback(async () => {
    const identifier = isEmail ? email : phoneNumber;
    if (!identifier || !rescheduleVerificationPurpose) return;
    setSending(true);
    setErrorMessage(null);
    try {
      await bookingFlowApi.sendVerification(
        isEmail
          ? { email, purpose: rescheduleVerificationPurpose }
          : { phoneNumber, purpose: rescheduleVerificationPurpose },
      );
      setCodeAlreadySent(false);
      setVerificationStarted(true);
      startResendCooldown();
    } catch (err: any) {
      // The code the customer already has still works, so this is recovery,
      // not a dead end: open the code form, hold the same cooldown, and say
      // which code to enter.
      if (isVerificationCodeAlreadySentError(err)) {
        setCodeAlreadySent(true);
        setVerificationStarted(true);
        startResendCooldown();
        return;
      }
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSending(false);
    }
  }, [
    email,
    isEmail,
    phoneNumber,
    rescheduleVerificationPurpose,
    startResendCooldown,
  ]);

  const handleReschedule = () => {
    void sendVerificationCode();
  };

  const handleVerify = async () => {
    const identifier = isEmail ? email : phoneNumber;
    if (!code || !identifier || !customerId) {
      if (!code) setCodeError(true);
      return;
    }
    setCodeError(false);
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const res = await bookingFlowApi.verifiedAppointmentHistory(
        isEmail
          ? { salonId: appointmentDetails.salonId, email, code }
          : { salonId: appointmentDetails.salonId, phoneNumber, code },
      );
      setState((prev) => ({
        route: RESCHEDULE_LIST,
        state: {
          ...prev.state,
          upcomingAppointments: res.data.upcomingAppointments,
          hasUpcomingAppointments: res.data.hasUpcomingAppointments,
          customerId: res.data.customerId ?? customerId,
          rescheduleVerificationCode: code,
          rescheduleVerificationPurpose:
            res.data.verificationPurpose ?? rescheduleVerificationPurpose,
        },
      }));
    } catch (err: any) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (isSubmitting) {
    return <Paragraph>{t.submittingAppointment}</Paragraph>;
  }
  if (verificationStarted) {
    return (
      <div className={styles.stepContainer}>
        <Paragraph className={styles.prompt}>
          {isEmail
            ? (t.verificationEmailSentMessage ??
              "A verification code has been sent to your email.")
            : t.verifyPhoneMessage}
        </Paragraph>
        <InlineNotice
          message={codeAlreadySent ? t.verificationAlreadySent : null}
        />
        <Form layout="vertical" className={styles.compactForm}>
          <Form.Item
            label={<Text strong>{t.verificationCode}</Text>}
            htmlFor="reschedule-code"
          >
            <VerificationCodeInput
              id="reschedule-code"
              size={MODAL_COMPONENT_SIZE}
              placeholder={t.codePlaceholder}
              value={code}
              status={codeError ? "error" : ""}
              onChange={(digits) => {
                setCode(digits);
                if (codeError) setCodeError(false);
              }}
            />
          </Form.Item>
        </Form>
        <InlineError message={errorMessage} />
        <div className={styles.actionRow}>
          <Flex vertical gap={8} style={{ width: "100%" }}>
            <Button
              type="primary"
              size={MODAL_COMPONENT_SIZE}
              loading={isSubmitting}
              disabled={isSubmitting || !code}
              onClick={handleVerify}
              block
            >
              {t.submit}
            </Button>
            <VerificationResendButton
              isCoolingDown={isResendCoolingDown}
              isSending={isSending}
              isSubmitting={isSubmitting}
              onResend={() => void sendVerificationCode()}
            />
          </Flex>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.stepContainer}>
      <Paragraph className={styles.prompt}>
        {t.existingAppointmentsGeneric}
      </Paragraph>

      <Flex vertical gap={"small"} style={{ width: "100%" }}>
        <Button
          size={MODAL_COMPONENT_SIZE}
          color="default"
          variant="solid"
          disabled={isSubmitting || !customerId}
          onClick={handleNewAppointment}
          block
        >
          {t.makeAnother}
        </Button>
        <Button
          color="default"
          variant="filled"
          size={MODAL_COMPONENT_SIZE}
          loading={isSending}
          disabled={isSubmitting || isSending || !rescheduleVerificationPurpose}
          onClick={handleReschedule}
          block
        >
          {t.reschedule}
        </Button>
      </Flex>
      <InlineError message={errorMessage} />
    </div>
  );
};

export default RescheduleMain;
